import type { ConnectionPositionPair } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';

import { PwTestIdDirective } from '@core/common-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import { HEADER_TEST_IDS } from '@core/tellent-assistant-util';
import {
  ConnectedOverlayDirective,
  DividerComponent,
  DropdownComponent,
  DropdownItemComponent,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
  OverlayOriginDirective,
  RtTooltip,
} from '@core/user-interface-legacy';

@Component({
  selector: 'tellent-assistant-header',
  imports: [
    ConnectedOverlayDirective,
    DividerComponent,
    DropdownComponent,
    DropdownItemComponent,
    HeadingComponent,
    IconButtonComponent,
    IconComponent,
    OverlayOriginDirective,
    PwTestIdDirective,
    TranslatePipe,
    RtTooltip,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.expanded]': 'expanded()',
    '[class.minimized]': 'minimized()',
    '(click)': 'headerClick.emit()',
  },
  templateUrl: './tellent-assistant-header.component.html',
  styleUrl: './tellent-assistant-header.component.less',
})
export class TellentAssistantHeaderComponent {
  protected readonly testIds = HEADER_TEST_IDS;
  protected readonly fullscreenIcon = computed(() =>
    this.expanded() ? 'fullscreen-exit' : 'fullscreen',
  );
  protected readonly fullscreenTooltip = computed(() =>
    this.expanded()
      ? 'tlc.tellent_assistant.header.tooltip_exit_fullscreen'
      : 'tlc.tellent_assistant.header.tooltip_fullscreen',
  );
  protected readonly minimizeIcon = computed(() => (this.minimized() ? 'expand-less' : 'remove'));
  protected readonly minimizeTooltip = computed(() =>
    this.minimized()
      ? 'tlc.tellent_assistant.header.tooltip_expand'
      : 'tlc.tellent_assistant.header.tooltip_minimize',
  );
  protected readonly menuOpen = signal<boolean>(false);
  protected readonly menuPositions = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
  ] satisfies ConnectionPositionPair[];
  protected readonly pinLabel = computed(() =>
    this.pinned()
      ? 'tlc.tellent_assistant.header.menu_unpin'
      : 'tlc.tellent_assistant.header.menu_pin',
  );

  public readonly menuTriggerOrigin = viewChild<OverlayOriginDirective>('menuTriggerOrigin');

  public readonly title = input.required<string>();
  public readonly inThread = input.required<boolean>();
  public readonly expanded = input.required<boolean>();
  public readonly minimized = input<boolean>(false);
  public readonly pinned = input<boolean>(false);
  public readonly windowControls = input<boolean>(true);

  public readonly menu = output<void>();
  public readonly newChat = output<void>();
  public readonly close = output<void>();
  public readonly expand = output<void>();
  public readonly collapse = output<void>();
  public readonly minimize = output<void>();
  public readonly restore = output<void>();
  public readonly headerClick = output<void>();
  public readonly pinThread = output<void>();

  protected toggleFullscreen(): void {
    this.expanded() ? this.collapse.emit() : this.expand.emit();
  }

  protected toggleMinimize(): void {
    this.minimized() ? this.restore.emit() : this.minimize.emit();
  }
}
